import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form, Input, Radio, Select, Button, Icon, Popconfirm } from 'antd';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { getLocale, Locale } from '@fxtr/i18n';
import { GROUPS } from '../../../../../../menuPermissions';

const { Option } = Select;
const { TextArea } = Input;
const FormItem = Form.Item;

import Modal from '../../../../components/Modal';
import { isPopupOpen, quoteSelector, getBookingSelector, getAuthUser } from '../selectors';
import {
  closePopupAction,
  saveWorkOperationAction,
  deleteWorkOperationAction
} from '../actions';
import {
  ADDITIONAL_DISCOUNT_TYPE,
  COUPON_TYPE,
  DISCOUNT_TYPE,
} from '../constants';

const DiscountInfoBox = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  color: #666;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const TotalDiscountRow = styled(DiscountRow)`
  background: #f5f5f5;
  color: #333;
  font-weight: 700;
  font-size: 15px;
  border-bottom: none;
`;

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
  font-size: 16px;
`;

const SectionSubTitle = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
`;

const ModalContent = styled.div`
`;

const ADDITIONAL_DISCOUNT_TITLES = {
  [Locale.FR_FR]: 'Remise supplémentaire',
  [Locale.EN_GB]: 'Additional Discount',
};

export const canDefineManualDiscount = (groups = []) => {
  const allowedGroups = [GROUPS.ADMIN, GROUPS.DEVELOPERS];

  return groups.some(group => allowedGroups.includes(String(group).toUpperCase()));
};

const AddDiscountPopup = ({
  isPopupOpen,
  booking,
  authUser,
  form,
  closePopup,
  saveWorkOperation,
  deleteWorkOperation,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [amountType, setAmountType] = useState('5');

  const isCSAdmin = canDefineManualDiscount(authUser?.groups);

  const campaign = booking?.campaign;
  const lineItems = booking?.quote?.lineItems || [];
  const additionalDiscountItem = lineItems.find(li => li.type === 'additional-discount');

  const hasAdditionalDiscount = !!additionalDiscountItem;
  const { getFieldDecorator, getFieldValue, validateFields, setFieldsValue } = form;

  useEffect(() => {
    if (isPopupOpen) {
      setEditMode(!hasAdditionalDiscount);
      if (hasAdditionalDiscount) {
        const discountAmount = Math.abs((additionalDiscountItem?.fixedCostWithVAT || 0)) / 100;
        const isPreset = discountAmount === 5 || discountAmount === 10;
        const newAmountType = isPreset ? discountAmount.toString() : 'manual';
        setAmountType(newAmountType);

        setTimeout(() => {
          const valuesToSet = {
            category: additionalDiscountItem?.category,
            comment: additionalDiscountItem?.comments?.[0]?.message,
          };
          if (!isPreset) {
            valuesToSet.manualAmount = discountAmount;
          } else {
            valuesToSet.amountType = newAmountType;
          }
          setFieldsValue(valuesToSet);
        }, 0);
      } else {
        setAmountType('5');
        setFieldsValue({
          amountType: '5',
          manualAmount: undefined,
          category: undefined,
          comment: undefined,
        });
      }
    }
  }, [isPopupOpen, hasAdditionalDiscount, additionalDiscountItem, setFieldsValue]);

  const onSave = (e) => {
    e.preventDefault();
    validateFields((err, values) => {
      if (!err) {
        const amount = amountType === 'manual' ? values.manualAmount : amountType;
        const locale = getLocale(booking?.segment);
        const discountTitle = ADDITIONAL_DISCOUNT_TITLES[locale] || ADDITIONAL_DISCOUNT_TITLES[Locale.EN_GB];

        const discountData = {
          title: discountTitle,
          workSummary: discountTitle,
          sku: 'additional-discount',
          type: ADDITIONAL_DISCOUNT_TYPE,
          // The saga setLineItemPriceInPence will convert these to pence,
          // so we send values in POUNDS
          fixedCostWithVAT: -Math.abs(parseFloat(amount)),
          accountCode: '0',
          vatExempt: false,
          quantity: 1,
          showCustomer: true,
          showGarage: false,
          category: values.category,
          source: `admin:${authUser?.username || 'unknown'}`,
          comments: [
            {
              message: values.comment,
              type: 'cm-additional-discount',
              updatedAt: Date.now(),
            },
            {
              message: values.category,
              type: 'cm-additional-discount-category',
              updatedAt: Date.now(),
            }
          ],
          key: additionalDiscountItem?.operationId,
        };

        saveWorkOperation(discountData);
        closePopup();
      }
    });
  };

  const onDelete = () => {
    if (additionalDiscountItem?.operationId) {
      deleteWorkOperation(additionalDiscountItem.operationId);
      closePopup();
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleAmountChange = (e) => {
    setAmountType(e.target.value);
    setFieldsValue({ manualAmount: undefined });
  };

  const handleDefineManually = () => {
    setAmountType('manual');
    setFieldsValue({ amountType: undefined });
  };

  const renderFooter = () => {
    if (editMode) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button key="cancel" onClick={closePopup}>
            Cancel
          </Button>
          <Button key="save" type="primary" onClick={onSave}>
            {hasAdditionalDiscount ? 'Save' : 'Add Coupon'}
          </Button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button key="cancel" onClick={closePopup}>
          Cancel
        </Button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasAdditionalDiscount && (
            <>
              <Popconfirm
                key="delete"
                title="Are you sure you want to delete this coupon?"
                onConfirm={onDelete}
                okText="Yes"
                cancelText="No"
              >
                <Button type="danger" ghost>
                  <Icon type="delete" /> Delete Coupon
                </Button>
              </Popconfirm>
              <Button key="edit" type="primary" onClick={toggleEditMode}>
                <Icon type="edit" /> Edit Coupon
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const otherDiscounts = lineItems.filter(li =>
    li.type !== ADDITIONAL_DISCOUNT_TYPE && (
      (li.source && li.source.startsWith('campaign:')) ||
      li.type === DISCOUNT_TYPE ||
      li.type === COUPON_TYPE ||
      li.sku === DISCOUNT_TYPE ||
      li.sku === COUPON_TYPE
    )
  );

  const totalOtherDiscountsValue = otherDiscounts.reduce((acc, li) =>
    acc + (Math.abs(li.fixedCostWithVAT || 0) / 100), 0
  );

  const otherDiscountNames = otherDiscounts.map(li => li.title || li.workSummary).filter(Boolean).join(', ');

  const manualAmountValue = getFieldValue('manualAmount');
  const selectedAmountValue = amountType === 'manual' ? (manualAmountValue || 0) : amountType;
  const totalDiscount = totalOtherDiscountsValue + parseFloat(selectedAmountValue.toString() || '0');

  const additionalDiscountAmount = (additionalDiscountItem?.fixedCostWithVAT || 0) / 100;
  const quoteTotal = (booking?.quote?.customerTotalPriceWithVAT || 0) / 100;
  // We need to exclude the current additional discount if we are editing it,
  // so we can calculate the real limit
  const baseTotalLimit = quoteTotal + Math.abs(additionalDiscountAmount);

  return (
    <Modal
      title={editMode ? (hasAdditionalDiscount ? 'Edit Coupon Code' : 'Add Discount') : 'Current Coupon Code'}
      width={500}
      visible={isPopupOpen}
      onCancel={closePopup}
      footer={renderFooter()}
    >
      <ModalContent>
        <Form layout="vertical">
          <SectionTitle>Define the discount</SectionTitle>
          <SectionSubTitle>
            {otherDiscounts.length > 0 ? (
              <span>
                This discount will be {editMode ? 'added' : 'combined'} with the existing discount{otherDiscounts.length > 1 ? 's' : ''} <b>({otherDiscountNames})</b>.
              </span>
            ) : (
              <span>This discount will be {editMode ? 'added' : 'applied'} to the quote.</span>
            )}
          </SectionSubTitle>

          <DiscountInfoBox>
            <DiscountRow>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 12, fontWeight: 500, color: '#333' }}>Amount</span>
                {amountType !== 'manual' && isCSAdmin && editMode && (
                  <span
                    onClick={handleDefineManually}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#1890ff',
                      fontSize: '13px'
                    }}
                  >
                    Define manually
                  </span>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {amountType === 'manual' ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FormItem style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: '120px', marginRight: '8px' }}>
                          {getFieldDecorator('manualAmount', {
                            rules: [
                              { required: true, message: 'Required' },
                              {
                                validator: (rule, value, callback) => {
                                  if (value === undefined || value === null || value === '') {
                                    callback();
                                    return;
                                  }
                                  const numericValue = parseFloat(value);
                                  if (isNaN(numericValue)) {
                                    callback('Please enter a valid number');
                                  } else if (numericValue <= 0) {
                                    callback('Amount must be greater than 0');
                                  } else if (numericValue > baseTotalLimit) {
                                    callback(`Maximum allowed: £${baseTotalLimit.toFixed(2)}`);
                                  } else {
                                    callback();
                                  }
                                }
                              }
                            ],
                          })(
                            <Input
                              type="number"
                              prefix="£"
                              placeholder="0.00"
                              disabled={!editMode}
                              size="small"
                              min={0.01}
                              step={0.01}
                            />
                          )}
                        </div>
                        {editMode && (
                          <Icon
                            type="delete"
                            style={{ cursor: 'pointer', color: '#666', marginTop: '4px' }}
                            onClick={() => {
                              setAmountType('5');
                              setFieldsValue({
                                amountType: '5',
                                manualAmount: undefined
                              });
                            }}
                          />
                        )}
                      </div>
                    </FormItem>
                  </div>
                ) : (
                  getFieldDecorator('amountType', {
                    initialValue: amountType,
                  })(
                    <Radio.Group onChange={handleAmountChange} disabled={!editMode}>
                      <Radio value="5">£5</Radio>
                      <Radio value="10">£10</Radio>
                    </Radio.Group>
                  )
                )}
              </div>
            </DiscountRow>

            {otherDiscounts.map((li, index) => (
              <DiscountRow key={li.operationId || index}>
                <span>
                  Existing Discount ({li.title || li.workSummary})
                </span>
                <span>£-{(Math.abs(li.fixedCostWithVAT || 0) / 100).toFixed(2)}</span>
              </DiscountRow>
            ))}

            <TotalDiscountRow>
              <span>Total Discount Value</span>
              <span>£-{totalDiscount.toFixed(2)}</span>
            </TotalDiscountRow>
          </DiscountInfoBox>

          <FormItem label={<Label>Discount Category</Label>} required={false}>
            {getFieldDecorator('category', {
              rules: [{ required: true, message: 'Please select a category' }],
            })(
              <Select placeholder="Select a category" disabled={!editMode}>
                <Option value="service_advisors">Service Advisors</Option>
                <Option value="customer_service">Customer Service</Option>
                <Option value="supply_team">Supply Team</Option>
                <Option value="return_to_workshop">Return to workshop</Option>
                <Option value="company_car">Company Car</Option>
                <Option value="cancellation_prevention">Cancellation Prevention</Option>
                <Option value="employee_discount">Employee Discount</Option>
                <Option value="other">Other</Option>
              </Select>
            )}
          </FormItem>

          <FormItem label={<Label>Explanation</Label>} required={false}>
            {getFieldDecorator('comment', {
              rules: [{ required: true, message: 'Please add an explanation' }],
            })(
              <TextArea
                placeholder="Add any comment for context..."
                disabled={!editMode}
                rows={4}
              />
            )}
          </FormItem>
        </Form>
      </ModalContent>
    </Modal>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    closePopup: () => dispatch(closePopupAction()),
    saveWorkOperation: (data) => dispatch(saveWorkOperationAction(data)),
    deleteWorkOperation: (operationId) => dispatch(deleteWorkOperationAction(operationId)),
  };
}

const mapStateToProps = createStructuredSelector({
  quote: quoteSelector(),
  isPopupOpen: isPopupOpen(),
  booking: getBookingSelector(),
  authUser: getAuthUser(),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
)(Form.create()(AddDiscountPopup));
